import { classifyPoint } from './classifyPoint'
import { formatChartDate, formatChartValue } from './chartFormatting'
import { getTimeSeriesSummary } from './getTimeSeriesSummary'
import { useChartSeries } from './ChartSeriesContext'
import { SummaryMode } from './types'
import type { ChartSeries } from './types'

import type { JSX } from 'react'

/** One "Followers: 1,234 latest, +56 over range" fragment per primary series. */
function summarise(series: ChartSeries[]): string {
  return series
    .map((s) => {
      const { latest, change } = getTimeSeriesSummary(s.data, s.summaryMode)
      if (latest == null) return `${s.label}: no data`
      const latestText = `${formatChartValue(latest, s.unit)} ${
        s.summaryMode === SummaryMode.TOTAL ? 'total' : 'latest'
      }`
      const changeText =
        change != null
          ? `, ${change >= 0 ? '+' : ''}${formatChartValue(change, s.unit)} over range`
          : ''
      return `${s.label}: ${latestText}${changeText}`
    })
    .join('. ')
}

interface ChartDataTableProps {
  /** Names the table (the chart's accessible label). */
  caption: string
  /** Month-only date labels for monthly-bucketed data. */
  monthly?: boolean
}

/**
 * A screen-reader data alternative for the chart, rendered inside a
 * {@link VisuallyHidden} wrapper by {@link LineChartView}. Reads the series
 * registry from context so it can't drift from what's drawn: one column per
 * primary series, one row per date, each cell the value or "No data available"
 * for a null (computed/missing) point.
 */
export function ChartDataTable({
  caption,
  monthly = false,
}: ChartDataTableProps): JSX.Element | null {
  const allSeries = useChartSeries()
  const series = allSeries.filter((s) => s.kind === 'primary')

  if (series.length === 0) return null

  const timestamps = Array.from(
    new Set(
      series.flatMap((s) => s.data.map((p) => new Date(p.date).getTime())),
    ),
  ).toSorted((a, b) => a - b)

  if (timestamps.length === 0) return null

  return (
    <table>
      <caption>{`${caption}. ${summarise(series)}`}</caption>
      <thead>
        <tr>
          <th scope="col">Date</th>
          {series.map((s) => (
            <th key={s.id} scope="col">
              {s.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {timestamps.map((ts) => (
          <tr key={ts}>
            <th scope="row">{formatChartDate(ts, monthly)}</th>
            {series.map((s) => {
              const point = classifyPoint(s, ts)
              return (
                <td key={s.id}>
                  {point.kind === 'value'
                    ? formatChartValue(point.value, s.unit)
                    : 'No data available'}
                </td>
              )
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
